import { arrayDiff, shallowEqual } from '../utility'

import { defineComponent } from 'vue'

import '../../prototypeExtensions/String'

export default defineComponent({
    data() {
        return {
            /** The following data is implemented in entriesCommonMixin.js
             *
             * entriesMap: {},
             * renderedEntriesMap: {},
             * fieldValuesMap: {},
             */

            /** The following data must be implemented by the mixin-implementing component
             *
             * @type {Record.<string, {matchMode: string, value: string[], num?: true}>}
             * filters: {},
             *
             * @type {Record.<string, string[]>}
             * userEnteredItemsMap: {},
             *
             * @type {{label: string, value: string, disabled?: boolean}[]}
             * columnDefaults: [] */

            /** A Map (Object) with ids of selected entries as keys, and 'true' as values.
             * For fast linear lookup of selected entries by using their ids.
             * @type {Record.<number, true>} */
            selectedRowIdsMap: {},

            /** Array of objects containing current column options for each column.
             * Shallow copy of this.columnDefaults.
             * @type {{label: string, value: string, disabled?: boolean}[]} */
            columnOptions: [],

            /** Array of column values (keys) selected from the column selector dropdown.
             * @type {string[]} */
            selectedColumns: [],

            /** A Map (Object) with 'value's of displayed columns as keys, and 'true' as values.
             * For fast linear lookup of displayed columns by using their values (keys).
             * @type {Record.<string, true>} */
            displayedColumns: {},

            /** A Map (Object) with current (in-edit) entry names, mapped by entry.id.
             * @type {Record.<number, string>} */
            cellEditValues: {},

            previewSizeItems: [
                {
                    value: 64,
                    text: Lang.langGet('Small')
                },
                {
                    value: 96,
                    text: Lang.langGet('Medium')
                },
                {
                    value: 128,
                    text: Lang.langGet('Large')
                }
            ],
            previewSizeModel: 64,

            t_clear_filters: Lang.langGet('Clear filters'),
            t_import: Lang.langGet('Import'),
            t_loading: Lang.langGet('Loading...'),

            t_no_matching_records_found: Lang.langGet('No matching records found.'),
            t_no_data_available_in_table: Lang.langGet('No data available in table.'),

            t_edit: Lang.langGet('Edit'),
            t_delete: Lang.langGet('Delete'),
            t_create_copy: Lang.langGet('Create Copy'),
            t_preview: Lang.langGet('Preview'),
            t_export: Lang.langGet('Export'),
            t_filter: Lang.langGet('Filter'),

            t_selected_rows_format: Lang.langGet('Selected %d rows.').removeDotsAtEnd(),
            t_selected_1_row: Lang.langGet('Selected 1 row.').removeDotsAtEnd()

        }
    },
    created: function() {

        this.columnOptions = this.columnDefaults.slice();

        const STORAGE_selectedColumnValues = localStorage.getItem(this.dataTableSelectedColumnValuesKey)

        if (STORAGE_selectedColumnValues != null) {
            this.selectedColumns = this.columnOptions
                .filter(columnOption => STORAGE_selectedColumnValues.includes(columnOption.value))
                .map(columnOption => columnOption.value)
        } else {
            this.selectedColumns = this.columnOptions.map(columnOption => columnOption.value)
        }

        const savedPreviewSize = +localStorage.getItem(this.localStoragePreviewSizeKey)
        const smallestPreviewSize = this.previewSizeItems[0].value

        this.previewSizeModel = savedPreviewSize >= smallestPreviewSize ? savedPreviewSize : smallestPreviewSize
    },
    methods: {
        onTableRowSelect(id) {
            this.selectedRowIdsMap = { ...this.selectedRowIdsMap, [id]: true }
        },
        onTableRowUnselect(id) {
            delete this.selectedRowIdsMap[id]
            this.selectedRowIdsMap = { ...this.selectedRowIdsMap }
        },
        onTableRowSelectAll() {
            const tempMap = {}
            Object.keys(this.entriesMap).forEach(entryId => tempMap[entryId] = true)
            this.selectedRowIdsMap = { ...tempMap }
        },
        onTableRowUnselectAll() {
            this.selectedRowIdsMap = {}
        },
        onSelectedEntriesChange(value) {
            const tempSelectedRowIdsMap = {}
            value.forEach(selectedEntry => {
                tempSelectedRowIdsMap[selectedEntry.id] = true
            })
            if (!shallowEqual(this.selectedRowIdsMap, tempSelectedRowIdsMap)) {
                this.selectedRowIdsMap = tempSelectedRowIdsMap
            }
        },
        async cellEditEnable(rowData) {
            this.cellEditValues = { ...this.cellEditValues, [rowData.id]: rowData.name }
        },
        cellEditCancel(entryId) {
            delete this.cellEditValues[entryId]
            this.cellEditValues = { ...this.cellEditValues }
        },
        rowNameUpdate({ id, name }) {
            const prevName = this.entriesMap[id].name
            this.entriesMap[id].name = name
            this.entriesMap[id].revision++

            // Update also the matching fieldValue entry with the new name, for dropdown suggestions

            const matchingFieldValuesEntry = this.fieldValuesMap.name.find(fieldValuesEntry => fieldValuesEntry.id === id)
            matchingFieldValuesEntry.value = name

            const filterNameEntryIndex = this.filters.name.value.indexOf(prevName)
            if (filterNameEntryIndex < 0) return
            this.filters.name.value.splice(filterNameEntryIndex, 1, name)
        },
        onRenderedEntriesUpdate(value) {
            if (!shallowEqual(this.renderedEntriesMap, value)) {
                this.renderedEntriesMap = value
            }
        },
    },
    computed: {
        selectedEntries() {
            return Object.keys(this.selectedRowIdsMap)
                .map(selectedRowId => this.entriesMap[selectedRowId])
                .filter(entry => entry != null)
        },
        selectedEntriesLengthString() {
            return this.someEntriesSelected ? this.selectedEntries.length.toString() : null
        },
        selectedEntriesHeaderLabel() {
            if(this.selectedEntriesLengthString == '1') {
                return this.t_selected_1_row
            } else {
                return this.t_selected_rows_format.replace('%d', this.selectedEntriesLengthString ?? '0')
            }
        },
        someEntriesSelected() {
            return this.selectedEntries.length > 0
        },
        dataTableSelectedColumnValuesKey() {
            return `${this.localStorageKeyPrefix}-selected-column-values.${Laravel.activeUser}.${Laravel.activeProject}`
        },
        allFilterableColumnsSelected() {
            return this.selectedColumns.length === this.columnOptions.length
        },
        localStoragePreviewSizeKey() {
            return `${this.localStorageKeyPrefix}-preview-size`
        }
    },
    watch: {
        selectedColumns(val, oldVal) {
            localStorage.setItem(this.dataTableSelectedColumnValuesKey, val/*.map(column => column.value)*/)
            const removedColumns = arrayDiff(oldVal, val)
            // Clear filter entry of each column that is removed, since
            // the filter box is not visible.
            removedColumns.forEach(this.clearFilterEntry)
        },
        previewSizeModel(val) {
            if (val > 0) localStorage.setItem(this.localStoragePreviewSizeKey, val)
        },

        /*renderedEntriesMap(val) {
            console.log('renderedEntriesMap', val)
        }*/
        /*selectedEntries(val) {
            console.log('selectedEntries', val)
        }*/
    }
})
